import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../services/api';
import { getToken, getUsuarioSessao, limparSessao } from '../utils/sessao';
import { BRAND_TITULO_ABA } from '../brand';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // SESSÃO POR GUIA: sessionStorage (isolado por guia) com fallback no
  // localStorage (sessão padrão do navegador, definida no login). Assim cada
  // guia pode operar numa organização diferente ao mesmo tempo.
  const [token, setToken] = useState(() => getToken());
  const [usuario, setUsuario] = useState(() => getUsuarioSessao());

  // global=true (login): vira a sessão padrão de guias novas também.
  // global=false (troca de organização): vale SÓ para esta guia.
  const _persistirSessao = useCallback((token, usuario, { global = true } = {}) => {
    sessionStorage.setItem('erp_token', token);
    sessionStorage.setItem('erp_usuario', JSON.stringify(usuario));
    if (global) {
      localStorage.setItem('erp_token', token);
      localStorage.setItem('erp_usuario', JSON.stringify(usuario));
    }
    setToken(token);
    setUsuario(usuario);
  }, []);

  const login = useCallback(async (email, senha) => {
    const { data } = await api.post('/auth/login', { email, senha });
    if (data.requer_selecao) {
      // Retorna dados para a tela de login exibir a seleção de organização
      return data;
    }
    _persistirSessao(data.token, data.usuario, { global: !data.usuario.sempre_pedir_login });
    return data.usuario;
  }, [_persistirSessao]);

  const selecionarOrganizacao = useCallback(async (usuario_id, organizacao_id) => {
    const { data } = await api.post('/auth/selecionar-organizacao', { usuario_id, organizacao_id });
    _persistirSessao(data.token, data.usuario, { global: !data.usuario.sempre_pedir_login });
    return data.usuario;
  }, [_persistirSessao]);

  // Troca de loja/organização SEM logout — vale SÓ PARA ESTA GUIA: outras
  // guias continuam na organização em que estão (pode-se trabalhar nas duas
  // empresas ao mesmo tempo, uma em cada guia).
  const trocarOrganizacao = useCallback(async (organizacao_id) => {
    const { data } = await api.post('/auth/trocar-organizacao', { organizacao_id });
    _persistirSessao(data.token, data.usuario, { global: false });
    return data.usuario;
  }, [_persistirSessao]);

  const logout = useCallback(() => {
    // Best-effort: avisa o backend pra encerrar a sessão (libera a vaga do
    // limite de acessos simultâneos na hora, em vez de esperar o token
    // expirar sozinho). Nunca bloqueia o logout local por causa disso —
    // rede caiu, token já vencido, tanto faz: limpa a sessão local de
    // qualquer jeito.
    api.post('/auth/logout').catch(() => {});
    limparSessao(); // limpa a guia e a sessão padrão (avisa as outras guias)
    setToken(null);
    setUsuario(null);
  }, []);

  // ── Título da guia acompanha a organização DESTA guia (sessão por guia):
  // "É MAIS GESTOR - R E DE O SILVA" (ou "É+ SISTEMA - ..." na marca É Mais
  // Sistema, ver brand.js) — facilita achar a empresa certa quando há uma
  // guia aberta para cada organização.
  useEffect(() => {
    document.title = usuario?.organizacao_nome
      ? `${BRAND_TITULO_ABA} - ${usuario.organizacao_nome}`
      : BRAND_TITULO_ABA;
  }, [usuario?.organizacao_nome]);

  // ── Guarda multi-guia: com a sessão POR GUIA, trocar de organização numa
  // guia não afeta as outras. Só o LOGOUT continua global (limpa o
  // localStorage) — o evento 'storage' dispara nas demais guias e elas avisam.
  const [sessaoExterna, setSessaoExterna] = useState(null); // { tipo: 'logout' } | null
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'erp_token' && !e.newValue && token) setSessaoExterna({ tipo: 'logout' });
    };
    window.addEventListener('storage', h);
    return () => window.removeEventListener('storage', h);
  }, [token]);

  // ── Aviso discreto de licença (Painel de Clientes) — "Sua licença expira
  // em X dias" / "está vencida, regularize em X dias". NUNCA bloqueia o
  // sistema (só o modal de SUSPENSO no login faz isso) — só um card
  // dispensável no canto da tela. Reaparece na próxima sessão/organização
  // (fecha só localmente, não silencia pra sempre — é lembrete de cobrança,
  // não notificação qualquer). Some sozinho se a licença voltar a ficar em
  // dia (usuario.licenca vira null num próximo login/troca).
  const [avisoFechado, setAvisoFechado] = useState(false);
  useEffect(() => { setAvisoFechado(false); }, [usuario?.licenca?.estado, usuario?.organizacao_id]);
  const licenca = usuario?.licenca;
  const mostrarAvisoLicenca = !avisoFechado && licenca && ['vencendo', 'em_tolerancia'].includes(licenca.estado);
  const corAviso = licenca?.estado === 'em_tolerancia'
    ? { bg: '#fff7ed', borda: '#fb923c', texto: '#9a3412' }
    : { bg: '#fefce8', borda: '#eab308', texto: '#854d0e' };

  return (
    <AuthContext.Provider value={{ token, usuario, login, selecionarOrganizacao, trocarOrganizacao, logout }}>
      {children}
      {sessaoExterna && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999999, background: 'rgba(15,23,42,.72)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 10, padding: '26px 30px', maxWidth: 460,
            textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,.35)', fontFamily: 'inherit',
          }}>
            <div style={{ fontSize: 34, marginBottom: 10 }}>⚠️</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>
              Sessão encerrada em outra guia
            </div>
            <div style={{ fontSize: 13.5, color: '#475569', marginBottom: 18 }}>
              O login foi encerrado em outra guia do navegador. Esta tela precisa ser recarregada.
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
                padding: '10px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}>
              Recarregar agora
            </button>
          </div>
        </div>
      )}
      {mostrarAvisoLicenca && (
        <div style={{
          position: 'fixed', right: 16, bottom: 16, zIndex: 99998, maxWidth: 320,
          background: corAviso.bg, border: `1px solid ${corAviso.borda}`, borderRadius: 8,
          padding: '10px 14px', boxShadow: '0 6px 20px rgba(0,0,0,.15)', fontFamily: 'inherit',
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <span style={{ fontSize: 16, lineHeight: 1 }}>⚠️</span>
          <div style={{ flex: 1, fontSize: 12.5, color: corAviso.texto, lineHeight: 1.4 }}>
            {licenca.mensagem || 'Sua licença precisa de atenção.'}
          </div>
          <button
            onClick={() => setAvisoFechado(true)}
            aria-label="Fechar aviso"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: corAviso.texto, fontSize: 14, lineHeight: 1, padding: 0 }}
          >
            ✕
          </button>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
